<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
	import {
		createPageSEO,
		createWebPageSchema,
		createBreadcrumbSchema,
		createFAQSchema
	} from '$lib/config/seo';

	import {
		Code2,
		Database,
		Server,
		CheckCircle2,
		TrendingUp,
		Clock,
		DollarSign,
		Target,
		Shield,
		GitBranch,
		Search,
		FileText,
		Award,
		BarChart3,
		Terminal,
		Bug,
		Lock
	} from '@lucide/svelte';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	let selectedRegion = $state('us');

	const salaryRegions = {
		us: {
			label: 'United States',
			currency: 'USD',
			ranges: [
				{
					level: 'Junior',
					range: '$70k - $110k',
					years: '0-2 years',
					items: [
						'1-2 years of contributions',
						'Solid fundamentals in 1 stack',
						'Growing project complexity'
					]
				},
				{
					level: 'Mid-Level',
					range: '$110k - $160k',
					years: '3-5 years',
					items: [
						'3-5 years active development',
						'Production system ownership',
						'Complex architecture contributions'
					],
					featured: true
				},
				{
					level: 'Senior',
					range: '$150k - $230k+',
					years: '6+ years',
					items: [
						'6+ years proven track record',
						'System architecture leadership',
						'Mentoring & code review depth'
					]
				}
			]
		},
		europe: {
			label: 'Europe',
			currency: 'EUR',
			ranges: [
				{
					level: 'Junior',
					range: '€40k - €60k',
					years: '0-2 years',
					items: [
						'1-2 years of contributions',
						'Solid fundamentals in 1 stack',
						'Growing project complexity'
					]
				},
				{
					level: 'Mid-Level',
					range: '€60k - €90k',
					years: '3-5 years',
					items: [
						'3-5 years active development',
						'Production system ownership',
						'Complex architecture contributions'
					],
					featured: true
				},
				{
					level: 'Senior',
					range: '€85k - €130k+',
					years: '6+ years',
					items: [
						'6+ years proven track record',
						'System architecture leadership',
						'Mentoring & code review depth'
					]
				}
			]
		},
		asia: {
			label: 'Asia Pacific',
			currency: 'USD',
			ranges: [
				{
					level: 'Junior',
					range: '$25k - $50k',
					years: '0-2 years',
					items: [
						'1-2 years of contributions',
						'Solid fundamentals in 1 stack',
						'Growing project complexity'
					]
				},
				{
					level: 'Mid-Level',
					range: '$45k - $80k',
					years: '3-5 years',
					items: [
						'3-5 years active development',
						'Production system ownership',
						'Complex architecture contributions'
					],
					featured: true
				},
				{
					level: 'Senior',
					range: '$75k - $120k+',
					years: '6+ years',
					items: [
						'6+ years proven track record',
						'System architecture leadership',
						'Mentoring & code review depth'
					]
				}
			]
		},
		india: {
			label: 'India',
			currency: 'INR',
			ranges: [
				{
					level: 'Junior',
					range: '₹6L - ₹12L',
					years: '0-2 years',
					items: [
						'1-2 years of contributions',
						'Solid fundamentals in 1 stack',
						'Growing project complexity'
					]
				},
				{
					level: 'Mid-Level',
					range: '₹12L - ₹25L',
					years: '3-5 years',
					items: [
						'3-5 years active development',
						'Production system ownership',
						'Complex architecture contributions'
					],
					featured: true
				},
				{
					level: 'Senior',
					range: '₹25L - ₹50L+',
					years: '6+ years',
					items: [
						'6+ years proven track record',
						'System architecture leadership',
						'Mentoring & code review depth'
					]
				}
			]
		}
	};

	const faqs = [
		{
			question: 'How does TalentProfile analyze backend developer skills?',
			answer:
				'We run automated analysis on public repositories using professional tooling - static analyzers, security scanners, and code quality metrics. We measure test coverage, error handling patterns, code complexity, security practices, and track how these metrics evolve over time. This gives us concrete indicators of code quality and best practices adherence.'
		},
		{
			question: "What's the average time to receive backend developer matches?",
			answer:
				'Most companies receive their first batch of 3-8 matched backend developers within 24-48 hours of posting a job description. For specialized tech stacks, it may take slightly longer. We prioritize developers with measurable indicators of experience in your specific technologies.'
		},
		{
			question: 'Can I see actual code samples before interviewing?',
			answer:
				'Yes. Every backend developer profile includes links to their analyzed GitHub repositories. You can review the actual code, our analysis summary showing code quality metrics, and specific examples of their work with your tech stack before making contact.'
		},
		{
			question: 'How accurate is the seniority assessment?',
			answer:
				'We analyze contribution timeline (years of active development), code quality progression over time, project complexity, architectural patterns, and best practices adherence. These are indicators, not guarantees - we recommend using our analysis as a strong filter, then validating fit through interviews.'
		},
		{
			question: "What if developers don't have much public code?",
			answer:
				"Our analysis works best for developers with active public GitHub presence. For candidates with primarily private repository experience, we recommend supplementing our analysis with traditional resume review and technical interviews. We're transparent about this limitation."
		},
		{
			question: 'Is there a limit to how many backend developer matches I can review?',
			answer:
				"No limits. We send you all candidates meeting your criteria with measurable technical indicators. For popular tech stacks like Node.js or Python, you might receive 15-25 matches. For specialized requirements, you'll get 3-8 more precisely matched candidates."
		}
	];

	const seo = createPageSEO({
		title: 'Hire Backend Developers - GitHub Analysis & Code Quality Metrics | TalentProfile',
		description:
			'Find backend developers through automated code analysis. Measure test coverage, security practices, and code quality with professional tooling. See real technical indicators before interviews.',
		canonical: '/hire-backend-developers',
		keywords:
			'hire backend developers, code quality analysis, GitHub developer search, technical assessment automation, backend engineer recruitment',
		ogImage: '/hire-backend-developers.png',
		structuredData: [
			createWebPageSchema({
				name: 'Hire Backend Developers',
				description:
					'Recruit backend developers based on automated code quality analysis and measurable technical indicators',
				url: '/hire-backend-developers'
			}),
			createBreadcrumbSchema([
				{ name: 'Home', url: '/' },
				{ name: 'Hire', url: '/hire' },
				{ name: 'Backend Developers' }
			]),
			createFAQSchema(faqs)
		]
	});

	const stats = [
		{
			value: '15+',
			label: 'Code Quality Metrics',
			description: 'Automated analysis points per developer profile'
		},
		{
			value: '2.5x',
			label: 'Faster Screening',
			description: 'Average time saved vs manual code review'
		},
		{
			value: '78%',
			label: 'Better Filtering',
			description: 'Companies report fewer unqualified interviews'
		},
		{ value: '24-48h', label: 'Match Delivery', description: 'First analyzed matches delivered' }
	];

	const analysisMetrics = [
		{
			icon: Terminal,
			category: 'Code Quality',
			metrics: [
				'Static analysis errors/warnings count',
				'Code complexity metrics (cyclomatic complexity)',
				'Code duplication percentage',
				'Dead code detection',
				'Linting rule violations'
			]
		},
		{
			icon: Shield,
			category: 'Security Practices',
			metrics: [
				'Hardcoded secrets detection',
				'SQL injection vulnerability patterns',
				'Unsafe API usage (eval, exec)',
				'Dependency vulnerability scanning',
				'Authentication/authorization patterns'
			]
		},
		{
			icon: Bug,
			category: 'Testing & Reliability',
			metrics: [
				'Test file presence and coverage',
				'Error handling consistency',
				'Logging implementation patterns',
				'CI/CD pipeline configuration',
				'Production monitoring setup'
			]
		},
		{
			icon: Database,
			category: 'Backend Patterns',
			metrics: [
				'Database query optimization patterns',
				'API design consistency (REST/GraphQL)',
				'Caching implementation',
				'Background job handling',
				'Migration file quality'
			]
		}
	];

	const realExamples = [
		{
			title: 'TypeScript Strictness',
			before: 'JavaScript with any types',
			after: 'Strict TypeScript with proper interfaces',
			indicator: 'Shows progression toward type safety'
		},
		{
			title: 'Error Handling',
			before: 'Empty catch blocks, crashes on null',
			after: 'Consistent error boundaries, proper logging',
			indicator: 'Production-ready code patterns'
		},
		{
			title: 'Test Coverage',
			before: 'No tests or minimal coverage',
			after: '70%+ coverage with integration tests',
			indicator: 'Professional quality standards'
		},
		{
			title: 'Code Organization',
			before: '5000-line files, mixed concerns',
			after: 'Feature-based structure, SRP adherence',
			indicator: 'Understanding of architecture'
		}
	];

	const painPoints = [
		{
			icon: FileText,
			problem: 'Resume Keyword Inflation',
			solutionTitle: 'Repository Analysis',
			reality: "Can't verify if 'Node.js expert' means 3 years production or 3 tutorial projects",
			solution:
				'Analyze actual Node.js repositories for complexity, patterns, test coverage over time'
		},
		{
			icon: Search,
			problem: 'Qualification Guesswork',
			solutionTitle: 'Automated Scoring',
			reality: 'Receive 200+ applications with no objective way to compare technical quality',
			solution: 'Automated code quality scoring ranks candidates by measurable technical indicators'
		},
		{
			icon: Clock,
			problem: 'Manual Code Review',
			solutionTitle: 'Pre-analyzed Profiles',
			reality: 'Spend days reviewing portfolios trying to assess code quality and best practices',
			solution:
				'Pre-analyzed profiles show code quality metrics, security issues, and test coverage upfront'
		},
		{
			icon: Target,
			problem: 'Hidden Technical Debt',
			solutionTitle: 'Quality Metrics',
			reality: "Discover in interviews that 'scalable architecture' has major code quality issues",
			solution:
				'Surface code duplication, complexity metrics, and anti-patterns before first contact'
		}
	];

	const whyItWorks = [
		{
			icon: Terminal,
			title: 'Professional Tooling',
			description:
				'We use the same static analyzers, linters, and security scanners that professional teams use in CI/CD. ESLint for JavaScript, Pylint for Python, RuboCop for Ruby - industry-standard tools measuring objective quality metrics.'
		},
		{
			icon: TrendingUp,
			title: 'Progression Tracking',
			description:
				'We analyze commits over time to see if code quality improves. A developer whose test coverage went from 0% to 70% over 2 years shows learning and growth. Static metrics at a single point miss this crucial signal.'
		},
		{
			icon: BarChart3,
			title: 'Concrete Measurements',
			description:
				"Not subjective opinions - we measure cyclomatic complexity, code duplication percentage, test coverage ratios, security vulnerability counts. These numbers tell a story about code quality that resumes can't capture."
		},
		{
			icon: Database,
			title: 'Pattern Recognition',
			description:
				'Does their Express.js code follow middleware patterns? Are database queries parameterized? Do they implement proper connection pooling? We detect production-ready patterns versus tutorial-level implementations through code structure analysis.'
		}
	];

	const benefits = [
		{
			icon: Code2,
			title: 'Automated Code Analysis',
			description: 'Professional static analysis tools measure objective quality',
			details: [
				'Static analyzer errors and warnings count',
				'Code complexity and maintainability index',
				'Duplication detection across files',
				'Best practices adherence scoring',
				'Framework-specific pattern analysis'
			]
		},
		{
			icon: Lock,
			title: 'Security Assessment',
			description: 'Identify security practices and potential vulnerabilities',
			details: [
				'Secret scanning (API keys, passwords)',
				'SQL injection risk patterns',
				'Dependency vulnerability detection',
				'Input validation implementation',
				'Authentication pattern analysis'
			]
		},
		{
			icon: Bug,
			title: 'Test Coverage Metrics',
			description: 'Measure testing discipline and reliability practices',
			details: [
				'Test file presence and organization',
				'Coverage percentage estimation',
				'Integration vs unit test balance',
				'CI/CD pipeline configuration',
				'Error handling consistency'
			]
		},
		{
			icon: Database,
			title: 'Backend Pattern Detection',
			description: 'Identify production-ready backend development patterns',
			details: [
				'Database query optimization attempts',
				'API design consistency (REST/GraphQL)',
				'Caching and performance considerations',
				'Logging and monitoring setup',
				'Migration file quality'
			]
		},
		{
			icon: TrendingUp,
			title: 'Quality Progression',
			description: 'Track how code quality improves over time',
			details: [
				'Error count reduction timeline',
				'Test coverage growth curve',
				'Complexity management evolution',
				'Security practice adoption',
				'Architecture pattern maturation'
			]
		},
		{
			icon: GitBranch,
			title: 'Contribution Analysis',
			description: 'Understand development activity and collaboration',
			details: [
				'Contribution frequency and consistency',
				'Repository ownership indicators',
				'Pull request review participation',
				'Open source involvement',
				'Documentation contributions'
			]
		}
	];

	const limitations = [
		{
			title: 'Best for Active Public GitHub Users',
			description:
				'Our analysis requires public repositories. Developers with primarily private work will have limited profiles.'
		},
		{
			title: 'Indicators, Not Guarantees',
			description:
				"Code metrics show technical patterns but can't measure problem-solving ability, communication, or team fit."
		},
		{
			title: 'Supplement with Interviews',
			description:
				'Use our analysis to filter candidates efficiently, then validate cultural fit and soft skills through interviews.'
		}
	];

	const comparisonPoints = [
		{
			traditional: "Search LinkedIn for 'backend developer' keywords",
			talentProfile:
				'Filter by measurable code quality metrics: test coverage >60%, security score >8/10, complexity under threshold'
		},
		{
			traditional: "Read through 200 generic resumes claiming '5 years Node.js experience'",
			talentProfile:
				'See actual Node.js projects with analysis: 4.2 avg complexity, 73% test coverage, 12 security issues resolved over time'
		},
		{
			traditional: 'Guess technical ability from job titles and company names',
			talentProfile:
				'Review automated analysis summary: static analyzer score, code duplication %, error handling patterns'
		},
		{
			traditional: "Hope their claimed 'database optimization expertise' is real",
			talentProfile:
				'See PostgreSQL usage patterns: parameterized queries, proper indexing in migrations, connection pooling implemented'
		},
		{
			traditional: 'Spend hours manually reviewing portfolios for code quality',
			talentProfile:
				'Pre-analyzed profiles show key metrics upfront: complexity trends, test coverage growth, security practices'
		},
		{
			traditional: 'Unclear seniority - inflated titles mask actual experience',
			talentProfile:
				'See technical progression: code quality improved 40% over 3 years, test coverage 0% → 75%, complexity reduced'
		}
	];

	const techStackExamples = [
		{
			category: 'Languages & Frameworks',
			technologies: 'Node.js, Python, Java, Go, Ruby, PHP, .NET, Rust, Elixir',
			note: 'Most developers specialize in 1-2 of these'
		},
		{
			category: 'Databases',
			technologies: 'PostgreSQL, MySQL, MongoDB, Redis, Elasticsearch, Cassandra, DynamoDB',
			note: 'Typically expertise in 2-3 database systems'
		},
		{
			category: 'Cloud & Infrastructure',
			technologies: 'AWS, GCP, Azure, Docker, Kubernetes, Terraform, Serverless',
			note: 'Usually focused on one primary cloud platform'
		},
		{
			category: 'APIs & Integration',
			technologies: 'REST, GraphQL, gRPC, WebSockets, Message Queues (RabbitMQ, Kafka)',
			note: 'Experience varies by project requirements'
		}
	];
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}
<div class="min-h-[calc(100dvh-527px)] bg-white">
	<!-- Hero Section -->
	<section class="bg-white px-4 pt-20 pb-16">
		<div class="mx-auto max-w-6xl">
			<div class="mb-12 text-center">
				<div
					class="mb-6 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-medium text-blue-600"
				>
					<Server class="h-4 w-4" />
					Backend Developer Recruitment
				</div>
				<h1 class="mb-6 text-4xl leading-tight font-bold text-gray-900 md:text-6xl">
					Hire Backend Developers<br />
					<span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Analyzed by Code Quality Tools</span
					>
				</h1>
				<p class="mx-auto mb-8 max-w-3xl text-xl text-gray-600">
					Stop guessing from resumes. Start reviewing automated analysis: test coverage metrics,
					security scans, code complexity scores, and quality progression over time. See technical
					indicators before the first interview.
				</p>
				<div class="flex flex-col justify-center gap-4 sm:flex-row">
					<a
						href="/drop-jd"
						class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-8 py-4 font-semibold text-white transition-colors hover:bg-blue-700"
					>
						<FileText class="h-5 w-5" />
						Post Job Description
					</a>
					<a
						href="/how-it-works"
						class="rounded-lg border-2 border-gray-300 bg-white px-8 py-4 font-semibold text-gray-900 transition-colors hover:bg-gray-50"
					>
						See How It Works
					</a>
				</div>
			</div>

			<!-- Stats -->
			<div class="mb-16 grid gap-6 md:grid-cols-4">
				{#each stats as stat (stat.label)}
					<div
						class="rounded-xl border border-gray-200 bg-white p-6 text-center transition-shadow hover:shadow-lg"
					>
						<div
							class="mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-4xl font-bold text-transparent"
						>
							{stat.value}
						</div>
						<div class="mb-1 text-sm font-semibold text-gray-900">{stat.label}</div>
						<div class="text-xs text-gray-600">{stat.description}</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Pain Points -->
	<section class="bg-gray-50 px-4 py-16">
		<div class="mx-auto max-w-7xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				The <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>Verification Problem</span
				> in Backend Hiring
			</h2>
			<p class="mx-auto mb-12 max-w-2xl text-center text-gray-600">
				Resume screening provides no objective way to verify claimed technical skills
			</p>
			<div class="grid gap-8 lg:grid-cols-2">
				<!-- Problems Side -->
				<div class="space-y-4">
					{#each painPoints as point (point.problem)}
						{@const Icon = point.icon}
						<div class="rounded-r-xl border-l-4 border-red-500 bg-red-50 p-6">
							<div class="mb-2 flex items-start gap-3">
								<Icon class="mt-1 h-5 w-5 shrink-0 text-red-600" />
								<div>
									<h3 class="mb-1 font-bold text-gray-900">{point.problem}</h3>
									<p class="text-sm text-gray-700 italic">"{point.reality}"</p>
								</div>
							</div>
						</div>
					{/each}
				</div>

				<!-- Solutions Side -->
				<div class="space-y-4">
					{#each painPoints as point (point.problem)}
						<div class="rounded-r-xl border-l-4 border-green-500 bg-green-50 p-6">
							<div class="flex items-start gap-3">
								<CheckCircle2 class="mt-1 h-5 w-5 shrink-0 text-green-600" />
								<div>
									<h3 class="mb-1 font-bold text-gray-900">{point.solutionTitle}</h3>
									<p class="text-sm text-gray-700">{point.solution}</p>
								</div>
							</div>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- Analysis Metrics -->
	<section class="bg-white px-4 py-16">
		<div class="mx-auto max-w-6xl">
			<div class="mb-12 text-center">
				<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
					What We <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Actually Measure</span
					>
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					Professional static analysis tools and security scanners run on every repository. These
					are the same tools engineering teams use in CI/CD pipelines.
				</p>
			</div>

			<div class="grid gap-8 md:grid-cols-2">
				{#each analysisMetrics as category (category.category)}
					{@const Icon = category.icon}
					<div
						class="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6"
					>
						<div class="mb-4 flex items-center gap-3">
							<div class="rounded-lg bg-blue-100 p-3">
								<Icon class="h-6 w-6 text-blue-600" />
							</div>
							<h3 class="text-xl font-bold text-gray-900">{category.category}</h3>
						</div>
						<ul class="space-y-2">
							{#each category.metrics as metric (metric)}
								<li class="flex items-start gap-2 text-sm text-gray-700">
									<CheckCircle2 class="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
									<span>{metric}</span>
								</li>
							{/each}
						</ul>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Real Examples -->
	<section class="bg-gray-50 px-4 py-16">
		<div class="mx-auto max-w-6xl">
			<div class="mb-12 text-center">
				<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
					<span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Real Indicators</span
					> We Track Over Time
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					Quality progression shows learning and professional growth better than any resume claim
				</p>
			</div>

			<div class="grid gap-6 md:grid-cols-2">
				{#each realExamples as example (example.title)}
					<div class="rounded-xl border border-gray-200 bg-white p-6">
						<h3 class="mb-4 text-lg font-bold text-gray-900">{example.title}</h3>
						<div class="space-y-3">
							<div class="flex items-start gap-3">
								<div
									class="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-red-100 text-xs font-semibold text-red-600"
								>
									Before
								</div>
								<p class="flex-1 pt-2 text-sm text-gray-700">{example.before}</p>
							</div>
							<div class="flex items-center justify-center">
								<TrendingUp class="h-5 w-5 text-blue-600" />
							</div>
							<div class="flex items-start gap-3">
								<div
									class="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-green-100 text-xs font-semibold text-green-600"
								>
									After
								</div>
								<p class="flex-1 pt-2 text-sm text-gray-700">{example.after}</p>
							</div>
							<div class="border-t border-gray-100 pt-2">
								<div class="flex items-start gap-2">
									<Award class="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
									<p class="text-sm font-medium text-blue-600">{example.indicator}</p>
								</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Benefits -->
	<section class="bg-white px-4 py-20">
		<div class="mx-auto max-w-6xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				What You Get with <span
					class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>TalentProfile</span
				>
			</h2>
			<p class="mx-auto mb-12 max-w-2xl text-center text-gray-600">
				Pre-analyzed developer profiles with concrete technical measurements
			</p>
			<div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
				{#each benefits as benefit (benefit.title)}
					{@const Icon = benefit.icon}
					<div
						class="rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-lg"
					>
						<div class="mb-4 w-fit rounded-lg bg-blue-100 p-3">
							<Icon class="h-6 w-6 text-blue-600" />
						</div>
						<h3 class="mb-2 text-xl font-bold text-gray-900">{benefit.title}</h3>
						<p class="mb-4 text-gray-600">{benefit.description}</p>
						<ul class="space-y-2">
							{#each benefit.details as detail (detail)}
								<li class="flex items-start gap-2 text-sm text-gray-700">
									<CheckCircle2 class="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
									<span>{detail}</span>
								</li>
							{/each}
						</ul>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Why It Works -->
	<section class="bg-gray-50 px-4 py-16">
		<div class="mx-auto max-w-6xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Why <span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>Automated Analysis</span
				> Works
			</h2>
			<p class="mx-auto mb-12 max-w-2xl text-center text-gray-600">
				Objective measurements beat subjective resume claims
			</p>
			<div class="grid gap-8 md:grid-cols-2">
				{#each whyItWorks as point (point.title)}
					{@const Icon = point.icon}

					<div class="rounded-xl border border-gray-200 bg-white p-8">
						<div class="mb-4 flex items-center gap-3">
							<div class="superellipse bg-blue-600 p-2">
								<Icon class="h-5 w-5 text-white" />
							</div>
							<h3 class="text-xl font-bold text-gray-900">{point.title}</h3>
						</div>
						<p class="leading-relaxed text-gray-700">{point.description}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Limitations -->
	<section class="bg-white px-4 py-16">
		<div class="mx-auto max-w-5xl">
			<div class="mb-12 text-center">
				<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
					What Our Analysis <span
						class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
						>Can and Can't Do</span
					>
				</h2>
				<p class="mx-auto max-w-3xl text-lg text-gray-600">
					We believe in transparency about what automated analysis provides
				</p>
			</div>

			<div class="mb-8 grid gap-6 md:grid-cols-3">
				{#each limitations as limitation (limitation.title)}
					<div class="rounded-r-xl border-l-4 border-yellow-500 bg-yellow-50 p-6">
						<h3 class="mb-2 font-bold text-gray-900">{limitation.title}</h3>
						<p class="text-sm text-gray-700">{limitation.description}</p>
					</div>
				{/each}
			</div>

			<div class="rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
				<h3 class="mb-3 text-2xl font-bold">Our Honest Recommendation</h3>
				<p class="leading-relaxed text-white opacity-90">
					Use TalentProfile to efficiently filter candidates by objective technical indicators,
					saving hours of manual screening. Then conduct interviews to assess problem-solving,
					communication, and cultural fit. We're a powerful first-pass filter, not a replacement for
					human judgment.
				</p>
			</div>
		</div>
	</section>

	<!-- Tech Stack Coverage -->
	<section class="bg-gray-50 px-4 py-16">
		<div class="mx-auto max-w-6xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Backend <span
					class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>Tech Stack Coverage</span
				>
			</h2>
			<p class="mx-auto mb-12 max-w-2xl text-center text-gray-600">
				We analyze code quality across all major backend technologies
			</p>
			<div class="mb-12 grid gap-6 md:grid-cols-2">
				{#each techStackExamples as stack (stack.category)}
					<div class="rounded-xl border border-gray-200 bg-white p-6">
						<h3 class="mb-2 text-lg font-bold text-gray-900">{stack.category}</h3>
						<p class="mb-2 text-gray-700">{stack.technologies}</p>
						<p class="text-sm text-gray-500 italic">{stack.note}</p>
					</div>
				{/each}
			</div>
			<div class="rounded-xl bg-blue-600 p-8 text-center text-white">
				<Database class="mx-auto mb-4 h-12 w-12 opacity-90" />
				<h3 class="mb-3 text-2xl font-bold">Need a Specialized Stack?</h3>
				<p class="mx-auto mb-6 max-w-2xl text-blue-100">
					Looking for Rust microservices experts? Elixir/Phoenix developers? GraphQL specialists?
					Our analysis adapts to different tech stacks - we measure quality indicators specific to
					each technology.
				</p>
				<a
					href="/drop-jd"
					class="inline-block rounded-lg bg-white px-8 py-3 font-semibold text-blue-600 transition-colors hover:bg-blue-50"
				>
					Post Your Requirements
				</a>
			</div>
		</div>
	</section>

	<!-- Comparison -->
	<section class="flex flex-col gap-16 bg-white px-4 py-16">
		<div class="mx-auto max-w-4xl">
			<h2 class="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				<span class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>Traditional Screening</span
				> vs Code Analysis
			</h2>
			<div class="space-y-6">
				{#each comparisonPoints as point (point.traditional)}
					<div
						class="rounded-xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-lg"
					>
						<div class="grid gap-6 md:grid-cols-2">
							<div class="flex items-start gap-3">
								<div
									class="superellipse mt-1 flex h-10 w-10 shrink-0 items-center justify-center bg-red-100"
								>
									<span class="text-xs font-bold text-red-600">✕</span>
								</div>
								<p class="text-gray-600">{point.traditional}</p>
							</div>
							<div class="flex items-start gap-3 md:border-l md:border-gray-200 md:pl-6">
								<div
									class="superellipse mt-1 flex h-10 w-10 shrink-0 items-center justify-center bg-green-100"
								>
									<CheckCircle2 class="h-4 w-4 text-green-600" />
								</div>
								<p class="font-medium text-gray-900">{point.talentProfile}</p>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>

		<div class="mx-auto max-w-4xl">
			<GlopayCtaHorizontal placement="hire-backend-developers-post-how-it-works" />
		</div>
	</section>

	<!-- Salary Ranges with Tabs -->
	<section class="bg-gray-50 px-4 py-16">
		<div class="mx-auto max-w-5xl">
			<h2 class="mb-4 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Backend Developer <span
					class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>Salary Ranges</span
				>
			</h2>
			<p class="mx-auto mb-8 max-w-2xl text-center text-gray-600">
				Typical compensation for backend developers by region (2025-2025)
			</p>

			<!-- Region Tabs -->
			<div class="mb-8 flex flex-wrap justify-center gap-2">
				{#each Object.entries(salaryRegions) as [key, region] (region.label)}
					<button
						onclick={() => (selectedRegion = key)}
						class={`rounded-lg px-6 py-3 font-semibold transition-all ${
							selectedRegion === key
								? 'bg-blue-600 text-white shadow-md'
								: 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-100'
						}`}
					>
						{region.label}
					</button>
				{/each}
			</div>

			<!-- Salary Cards -->
			<div class="grid gap-6 md:grid-cols-3">
				{#each salaryRegions[selectedRegion as keyof typeof salaryRegions].ranges as range (range.range)}
					<div
						class={`rounded-xl border-2 p-6 ${
							range.featured ? 'relative border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
						}`}
					>
						{#if range.featured}
							<div class="absolute -top-3 left-1/2 -translate-x-1/2">
								<span class="superellipse bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
									>Most Common</span
								>
							</div>
						{/if}
						<div
							class={`mb-2 text-sm font-semibold ${
								range.featured ? 'text-blue-700' : 'text-gray-500'
							}`}
						>
							{range.level} Backend Developer
						</div>
						<div class="mb-1 text-3xl font-bold text-gray-900">{range.range}</div>
						<div class="mb-4 text-sm text-gray-600">{range.years}</div>
						<ul class="space-y-2 text-sm text-gray-700">
							{#each range.items as item (item)}
								<li class="flex items-start gap-2">
									<CheckCircle2 class="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
									<span>{item}</span>
								</li>
							{/each}
						</ul>
					</div>
				{/each}
			</div>

			<p class="mt-6 text-center text-sm text-gray-600">
				Ranges vary by city, company size, and tech stack specialization. Remote positions typically
				offer 70-90% of local rates.
			</p>
		</div>
	</section>

	<!-- FAQ -->
	<section class="bg-white px-4 py-20">
		<div class="mx-auto max-w-4xl">
			<h2 class="mb-12 text-center text-3xl font-bold text-gray-900 md:text-4xl">
				Frequently Asked <span
					class="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
					>Questions</span
				>
			</h2>
			<div class="space-y-6">
				{#each faqs as faq (faq.question)}
					<div class="rounded-xl border border-gray-200 bg-white p-6">
						<h3 class="mb-3 text-lg font-bold text-gray-900">{faq.question}</h3>
						<p class="leading-relaxed text-gray-700">{faq.answer}</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA -->
	<section class="bg-gray-50 px-4 py-20">
		<div
			class="mx-auto max-w-4xl rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-12 text-center text-white shadow-xl"
		>
			<h2 class="mb-6 text-3xl font-bold md:text-4xl">
				Ready to Screen Backend Developers Faster?
			</h2>
			<p class="mx-auto mb-8 max-w-2xl text-xl text-white opacity-90">
				Post your job description. Get pre-analyzed developer profiles with code quality metrics,
				test coverage scores, and security assessments within 24-48 hours. Free forever.
			</p>
			<div class="flex flex-col justify-center gap-4 sm:flex-row">
				<a
					href="/drop-jd"
					class="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-8 py-4 font-semibold text-blue-600 transition-colors hover:bg-gray-100"
				>
					<FileText class="h-5 w-5" />
					Post Job Description
				</a>
				<a
					href="/hire"
					class="rounded-lg border-2 border-white bg-transparent px-8 py-4 font-semibold text-white transition-colors hover:bg-white hover:text-blue-600"
				>
					Learn More About Hiring
				</a>
			</div>
			<p class="mt-6 flex items-center justify-center gap-2 text-sm text-white opacity-75">
				<DollarSign class="h-4 w-4" /> No subscription fees • No per-hire charges • No hidden costs
			</p>
		</div>
	</section>
</div>
